import type { CmpNewsletterFieldsFragment, CmpOutboundEmailFieldsFragment, PreviewCmpNewsletterRecipientsQuery } from "../../../graphql/types";
import {
  EmailCampaign,
  EmailStatus,
  getEmailTypeLabel,
  getStatusLabel,
} from "./emailMockData";

export type EmailActionKey =
  | "preview"
  | "edit"
  | "duplicate"
  | "schedule"
  | "viewAnalytics"
  | "viewOpenList"
  | "resend"
  | "sendNow"
  | "cancelSchedule"
  | "removeFromNewsroom"
  | "delete";

export type EmailActionBehavior = Exclude<EmailActionKey, "edit"> | "open";

// Keep the existing actions for statuses which have no design example.
const existingRecordActions: ReadonlyArray<EmailActionKey> = [
  "preview", "edit", "duplicate", "schedule", "viewAnalytics", "viewOpenList", "removeFromNewsroom", "delete",
];

// Remove from Newsroom is in every status list and shows only for a
// published newsroom record (isEmailActionHidden).
export const emailActionsByStatus: Readonly<Record<EmailStatus, ReadonlyArray<EmailActionKey>>> = {
  draft: ["preview", "edit", "schedule", "duplicate", "removeFromNewsroom", "delete"],
  sent: ["preview", "edit", "viewAnalytics", "viewOpenList", "duplicate", "removeFromNewsroom", "delete"],
  failed: ["preview", "resend", "duplicate", "removeFromNewsroom", "delete"],
  scheduled: ["preview", "edit", "sendNow", "cancelSchedule", "duplicate", "removeFromNewsroom", "delete"],
  review: existingRecordActions,
  sending: ["preview", "duplicate", "viewAnalytics", "viewOpenList", "removeFromNewsroom"],
  canceled: ["preview", "duplicate", "removeFromNewsroom", "delete"],
};

export const isEmailActionHidden = (
  action: EmailActionKey,
  email: Pick<EmailCampaign, "newsroomStatus">
): boolean => action === "removeFromNewsroom" && email.newsroomStatus !== "published";

export const getEmailActionBehavior = (
  action: EmailActionKey
): EmailActionBehavior => {
  return action === "edit" ? "open" : action;
};

export const getEmailManageStep = (action: "edit" | "resend"): 1 | 4 =>
  action === "resend" ? 4 : 1;

export const validateEmailSchedule = (scheduledAt: number | null, now = Date.now()): string | null => {
  if (scheduledAt === null || !Number.isFinite(scheduledAt)) return "Choose a Scheduled Date and Scheduled Time.";
  if (scheduledAt <= now) return "Choose a date and time in the future.";
  return null;
};

type ScheduledSuccessMessageInput = {
  name: string;
  scheduledAtUtcMilli: number;
  timeZone?: string | null;
};

// Design copy: '{name} has been scheduled for {date} at {time}.' The date and
// time use the chamber time zone that the schedule was entered in.
export const buildEmailScheduledSuccessMessage = ({
  name,
  scheduledAtUtcMilli,
  timeZone,
}: ScheduledSuccessMessageInput): string => {
  const zone = timeZone ? { timeZone } : {};
  const date = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", ...zone }).format(scheduledAtUtcMilli);
  const time = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", ...zone }).format(scheduledAtUtcMilli);
  return `${name} has been scheduled for ${date} at ${time}.`;
};

export type EmailDatePreset = "last7" | "last30" | "last90" | "fiscalYear" | "custom";

export const getEmailDatePresetStart = (
  preset: Exclude<EmailDatePreset, "custom">,
  fiscalYearStart: number,
  now = Date.now()
): number => {
  if (preset === "fiscalYear") return fiscalYearStart;
  const days = preset === "last7" ? 7 : preset === "last30" ? 30 : 90;
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - days + 1);
  return start.getTime();
};

export type EmailManageSort = { column: string | null; direction: "asc" | "desc" };
export const nextEmailManageSort = (current: EmailManageSort, column: string): EmailManageSort => {
  if (current.column !== column) return { column, direction: "asc" };
  if (current.direction === "asc") return { column, direction: "desc" };
  return { column: null, direction: "asc" };
};

const rate = (count: number, total: number) => total ? Math.round(count / total * 100) : 0;
const csvCell = (value: string | number): string => {
  const text = String(value);
  // Spreadsheet programs must not treat saved campaign text as a formula.
  const safe = /^[\s]*[=+@-]/.test(text) ? `'${text}` : text;
  return `"${safe.replace(/"/g, '""')}"`;
};

export const buildEmailManageCsv = (emails: EmailCampaign[], kind: "all" | "opens" | "clicks"): string => {
  const headers = kind === "all"
    ? ["Communication Type", "Campaign Name", "Email Subject", "Sent Date", "Recipients", "Open Rate", "Click Rate", "Status"]
    : ["Campaign Name", kind === "opens" ? "Open Rate" : "Click Rate", kind === "opens" ? "Open %" : "Click %"];
  const rows = emails.map((email) => {
    const denominator = email.sentRecipients || email.intendedRecipients;
    const name = email.category || email.subject;
    return kind === "all"
      ? [getEmailTypeLabel(email.emailType), name, email.subject, email.sentAt || email.scheduledAt || "", email.audienceLabel || email.intendedRecipients,
        `${email.openCount}/${denominator}`, `${email.clickCount}/${denominator}`, getStatusLabel(email.status)]
      : [name, `${kind === "opens" ? email.openCount : email.clickCount}/${denominator}`, `${rate(kind === "opens" ? email.openCount : email.clickCount, denominator)}%`];
  });
  return [headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
};

type OpenListDelivery = Pick<CmpOutboundEmailFieldsFragment,
  "id" | "kind" | "toEmail" | "intendedToEmail" | "openedAtUtcMilli" | "sentAtUtcMilli" | "recipientOverrideUsed">;
type OpenListSubscriber = { email: string; name?: string | null };

export const buildEmailOpenList = (deliveries: readonly OpenListDelivery[], subscribers: readonly OpenListSubscriber[]) => {
  const names = new Map(subscribers.map((subscriber) => [subscriber.email.trim().toLowerCase(), subscriber.name || ""]));
  return deliveries.filter((delivery) => delivery.kind === "NEWSLETTER_CAMPAIGN" && typeof delivery.openedAtUtcMilli === "number")
    .map((delivery) => ({
      ...delivery,
      name: names.get((delivery.intendedToEmail || delivery.toEmail).trim().toLowerCase()) || "",
    })).sort((left, right) => (right.openedAtUtcMilli || 0) - (left.openedAtUtcMilli || 0));
};

type ManageRecipientPreview = PreviewCmpNewsletterRecipientsQuery["previewCmpNewsletterRecipients"];
export const getManageSendActualRecipients = (preview: Pick<ManageRecipientPreview, "recipientOverrideUsed" | "actualTestRecipientEmail" | "recipients">): string[] =>
  Array.from(new Set(preview.recipients.filter((recipient) => recipient.status === "VALID").map((recipient) =>
    preview.recipientOverrideUsed
      ? recipient.actualTestEmail || preview.actualTestRecipientEmail
      : recipient.intendedEmail || recipient.email
  ).filter((email): email is string => Boolean(email))));


export const buildEmailOpenListCsv = (rows: NonNullable<CmpNewsletterFieldsFragment["analytics"]>["recipients"], timeZone: string): string => [
  ["Subscriber", "Email Address", "Company", "Status", "Opens", "Last Opened"],
  ...rows.map(row => [row.name || "Not recorded", row.email, row.companyName || "Not recorded", row.status, row.openCount ?? "Unavailable",
    row.lastOpenedAtUtcMilli ? new Date(row.lastOpenedAtUtcMilli).toLocaleString("en-CA", { timeZone }) : "Unavailable"]),
].map(row => row.map(csvCell).join(",")).join("\n");
